export interface AthleticsDraftSchool {
  id: string;
  name: string;
  fileName: string;
  sports: Array<{
    AthleticsSchoolName: string;
    AthleticsGender: string;
    AthleticsSport: string;
  }>;
}

export class AthleticsDraftService {
  // List of Canadian athletics draft schools
  private static readonly CANADIAN_ATHLETICS_DRAFT_SCHOOLS = [
    'camosun-college-athletics.json'
  ];

  // List of US athletics draft schools
  private static readonly US_ATHLETICS_DRAFT_SCHOOLS = [
    'arizona-state-university-athletics.json',
    'auburn-university-athletics.json',
    'babson-college-athletics.json'
  ];

  // Get Canadian athletics draft schools
  static async getCanadianAthleticsDraftSchools(): Promise<AthleticsDraftSchool[]> {
    const athleticsDraftSchools: AthleticsDraftSchool[] = [];

    for (const fileName of this.CANADIAN_ATHLETICS_DRAFT_SCHOOLS) {
      try {
        // In a real implementation, this would fetch from the athleticsToBeReviewedCanada folder
        // For now, we'll use the data we know exists
        const schoolName = fileName
          .replace('-athletics.json', '')
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');

        // Sample data for Camosun College
        const sportsData = fileName === 'camosun-college-athletics.json' ? [
          {
            AthleticsSchoolName: "Camosun College",
            AthleticsGender: "M",
            AthleticsSport: "Basketball"
          },
          {
            AthleticsSchoolName: "Camosun College",
            AthleticsGender: "W",
            AthleticsSport: "Basketball"
          },
          {
            AthleticsSchoolName: "Camosun College",
            AthleticsGender: "M",
            AthleticsSport: "Volleyball"
          },
          {
            AthleticsSchoolName: "Camosun College",
            AthleticsGender: "W",
            AthleticsSport: "Volleyball"
          },
          {
            AthleticsSchoolName: "Camosun College",
            AthleticsGender: "M",
            AthleticsSport: "Golf"
          },
          {
            AthleticsSchoolName: "Camosun College",
            AthleticsGender: "W",
            AthleticsSport: "Golf"
          }
        ] : [];

        athleticsDraftSchools.push({
          id: fileName.replace('.json', ''),
          name: schoolName,
          fileName: fileName,
          sports: sportsData
        });
      } catch (error) {
        console.error(`Error processing ${fileName}:`, error);
      }
    }

    return athleticsDraftSchools;
  }

  // Get US athletics draft schools
  static async getUSAthleticsDraftSchools(): Promise<AthleticsDraftSchool[]> {
    const athleticsDraftSchools: AthleticsDraftSchool[] = [];

    for (const fileName of this.US_ATHLETICS_DRAFT_SCHOOLS) {
      try {
        const schoolName = fileName
          .replace('-athletics.json', '')
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');

        // Sample data for Arizona State University
        let sportsData = fileName === 'arizona-state-university-athletics.json' ? [
          {
            AthleticsSchoolName: "Arizona State University",
            AthleticsGender: "M",
            AthleticsSport: "Baseball"
          },
          {
            AthleticsSchoolName: "Arizona State University",
            AthleticsGender: "M",
            AthleticsSport: "Basketball"
          },
          {
            AthleticsSchoolName: "Arizona State University",
            AthleticsGender: "W",
            AthleticsSport: "Basketball"
          },
          {
            AthleticsSchoolName: "Arizona State University",
            AthleticsGender: "M",
            AthleticsSport: "Cross Country"
          },
          {
            AthleticsSchoolName: "Arizona State University",
            AthleticsGender: "W",
            AthleticsSport: "Cross Country"
          },
          {
            AthleticsSchoolName: "Arizona State University",
            AthleticsGender: "M",
            AthleticsSport: "Football"
          },
          {
            AthleticsSchoolName: "Arizona State University",
            AthleticsGender: "M",
            AthleticsSport: "Golf"
          },
          {
            AthleticsSchoolName: "Arizona State University",
            AthleticsGender: "W",
            AthleticsSport: "Golf"
          },
          {
            AthleticsSchoolName: "Arizona State University",
            AthleticsGender: "M",
            AthleticsSport: "Ice Hockey"
          },
          {
            AthleticsSchoolName: "Arizona State University",
            AthleticsGender: "M",
            AthleticsSport: "Swimming & Diving"
          },
          {
            AthleticsSchoolName: "Arizona State University",
            AthleticsGender: "W",
            AthleticsSport: "Swimming & Diving"
          },
          {
            AthleticsSchoolName: "Arizona State University",
            AthleticsGender: "M",
            AthleticsSport: "Track & Field"
          },
          {
            AthleticsSchoolName: "Arizona State University",
            AthleticsGender: "W",
            AthleticsSport: "Track & Field"
          },
          {
            AthleticsSchoolName: "Arizona State University",
            AthleticsGender: "M",
            AthleticsSport: "Wrestling"
          },
          {
            AthleticsSchoolName: "Arizona State University",
            AthleticsGender: "W",
            AthleticsSport: "Beach Volleyball"
          },
          {
            AthleticsSchoolName: "Arizona State University",
            AthleticsGender: "W",
            AthleticsSport: "Gymnastics"
          },
          {
            AthleticsSchoolName: "Arizona State University",
            AthleticsGender: "W",
            AthleticsSport: "Lacrosse"
          },
          {
            AthleticsSchoolName: "Arizona State University",
            AthleticsGender: "W",
            AthleticsSport: "Soccer"
          },
          {
            AthleticsSchoolName: "Arizona State University",
            AthleticsGender: "W",
            AthleticsSport: "Softball"
          },
          {
            AthleticsSchoolName: "Arizona State University",
            AthleticsGender: "W",
            AthleticsSport: "Tennis"
          },
          {
            AthleticsSchoolName: "Arizona State University",
            AthleticsGender: "W",
            AthleticsSport: "Triathlon"
          },
          {
            AthleticsSchoolName: "Arizona State University",
            AthleticsGender: "W",
            AthleticsSport: "Volleyball"
          },
          {
            AthleticsSchoolName: "Arizona State University",
            AthleticsGender: "W",
            AthleticsSport: "Water Polo"
          }
        ] : [];

        // Add Auburn University data
        if (fileName === 'auburn-university-athletics.json') {
          sportsData = [
            {
              AthleticsSchoolName: "Auburn University",
              AthleticsGender: "M",
              AthleticsSport: "Baseball"
            },
            {
              AthleticsSchoolName: "Auburn University",
              AthleticsGender: "M",
              AthleticsSport: "Basketball"
            },
            {
              AthleticsSchoolName: "Auburn University",
              AthleticsGender: "M",
              AthleticsSport: "Football"
            },
            {
              AthleticsSchoolName: "Auburn University",
              AthleticsGender: "M",
              AthleticsSport: "Golf"
            },
            {
              AthleticsSchoolName: "Auburn University",
              AthleticsGender: "M",
              AthleticsSport: "Swimming & Diving"
            },
            {
              AthleticsSchoolName: "Auburn University",
              AthleticsGender: "M",
              AthleticsSport: "Tennis"
            },
            {
              AthleticsSchoolName: "Auburn University",
              AthleticsGender: "M",
              AthleticsSport: "Track & Field, XC"
            },
            {
              AthleticsSchoolName: "Auburn University",
              AthleticsGender: "W",
              AthleticsSport: "Basketball"
            },
            {
              AthleticsSchoolName: "Auburn University",
              AthleticsGender: "W",
              AthleticsSport: "Equestrian"
            },
            {
              AthleticsSchoolName: "Auburn University",
              AthleticsGender: "W",
              AthleticsSport: "Golf"
            },
            {
              AthleticsSchoolName: "Auburn University",
              AthleticsGender: "W",
              AthleticsSport: "Gymnastics"
            },
            {
              AthleticsSchoolName: "Auburn University",
              AthleticsGender: "W",
              AthleticsSport: "Soccer"
            },
            {
              AthleticsSchoolName: "Auburn University",
              AthleticsGender: "W",
              AthleticsSport: "Softball"
            },
            {
              AthleticsSchoolName: "Auburn University",
              AthleticsGender: "W",
              AthleticsSport: "Swimming & Diving"
            },
            {
              AthleticsSchoolName: "Auburn University",
              AthleticsGender: "W",
              AthleticsSport: "Tennis"
            },
            {
              AthleticsSchoolName: "Auburn University",
              AthleticsGender: "W",
              AthleticsSport: "Track & Field, XC"
            },
            {
              AthleticsSchoolName: "Auburn University",
              AthleticsGender: "W",
              AthleticsSport: "Volleyball"
            }
          ];
        }

        // Add Babson College data
        if (fileName === 'babson-college-athletics.json') {
          sportsData = [
            {
              AthleticsSchoolName: "Babson College",
              AthleticsGender: "M",
              AthleticsSport: "Alpine Skiing"
            },
            {
              AthleticsSchoolName: "Babson College",
              AthleticsGender: "M",
              AthleticsSport: "Baseball"
            },
            {
              AthleticsSchoolName: "Babson College",
              AthleticsGender: "M",
              AthleticsSport: "Basketball"
            },
            {
              AthleticsSchoolName: "Babson College",
              AthleticsGender: "M",
              AthleticsSport: "Cross Country"
            },
            {
              AthleticsSchoolName: "Babson College",
              AthleticsGender: "M",
              AthleticsSport: "Golf"
            },
            {
              AthleticsSchoolName: "Babson College",
              AthleticsGender: "M",
              AthleticsSport: "Ice Hockey"
            },
            {
              AthleticsSchoolName: "Babson College",
              AthleticsGender: "M",
              AthleticsSport: "Lacrosse"
            },
            {
              AthleticsSchoolName: "Babson College",
              AthleticsGender: "M",
              AthleticsSport: "Soccer"
            },
            {
              AthleticsSchoolName: "Babson College",
              AthleticsGender: "M",
              AthleticsSport: "Swimming & Diving"
            },
            {
              AthleticsSchoolName: "Babson College",
              AthleticsGender: "M",
              AthleticsSport: "Tennis"
            },
            {
              AthleticsSchoolName: "Babson College",
              AthleticsGender: "M",
              AthleticsSport: "Track & Field"
            },
            {
              AthleticsSchoolName: "Babson College",
              AthleticsGender: "W",
              AthleticsSport: "Alpine Skiing"
            },
            {
              AthleticsSchoolName: "Babson College",
              AthleticsGender: "W",
              AthleticsSport: "Basketball"
            },
            {
              AthleticsSchoolName: "Babson College",
              AthleticsGender: "W",
              AthleticsSport: "Cross Country"
            },
            {
              AthleticsSchoolName: "Babson College",
              AthleticsGender: "W",
              AthleticsSport: "Field Hockey"
            },
            {
              AthleticsSchoolName: "Babson College",
              AthleticsGender: "W",
              AthleticsSport: "Golf"
            },
            {
              AthleticsSchoolName: "Babson College",
              AthleticsGender: "W",
              AthleticsSport: "Lacrosse"
            },
            {
              AthleticsSchoolName: "Babson College",
              AthleticsGender: "W",
              AthleticsSport: "Soccer"
            },
            {
              AthleticsSchoolName: "Babson College",
              AthleticsGender: "W",
              AthleticsSport: "Softball"
            },
            {
              AthleticsSchoolName: "Babson College",
              AthleticsGender: "W",
              AthleticsSport: "Swimming & Diving"
            },
            {
              AthleticsSchoolName: "Babson College",
              AthleticsGender: "W",
              AthleticsSport: "Tennis"
            },
            {
              AthleticsSchoolName: "Babson College",
              AthleticsGender: "W",
              AthleticsSport: "Track & Field"
            },
            {
              AthleticsSchoolName: "Babson College",
              AthleticsGender: "W",
              AthleticsSport: "Volleyball"
            }
          ];
        }

        athleticsDraftSchools.push({
          id: fileName.replace('.json', ''),
          name: schoolName,
          fileName: fileName,
          sports: sportsData
        });
      } catch (error) {
        console.error(`Error processing ${fileName}:`, error);
      }
    }

    return athleticsDraftSchools;
  }

  // Get athletics draft schools based on country
  static async getAthleticsDraftSchools(country: 'Canada' | 'US'): Promise<AthleticsDraftSchool[]> {
    if (country === 'Canada') {
      return this.getCanadianAthleticsDraftSchools();
    } else {
      return this.getUSAthleticsDraftSchools();
    }
  }
}
